import { FunctionComponent, useEffect, useState } from 'react'
import styles from './staff.module.scss'
import { useLazyQuery } from '@apollo/client'
import { GET_STAFF_LEVELS } from '@/modules/API/staff/staffLevels'
import { StaffLevel } from '@/types/StaffLevel'
import Title from '@/components/_custom/Title/title'
import Desktop from '@/modules/desktopMobile/desktop'
import { QuickSearchToolbar, StyledDataGrid } from '@/components/_custom/DataGrid'
import { frFR } from '@mui/x-data-grid/locales'
import Mobile from '@/modules/desktopMobile/mobile'
import { useTranslation } from 'react-i18next'
import { GridColDef } from '@mui/x-data-grid'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { ArrowBack, Close, Edit, LinkOff } from '@mui/icons-material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditStaffDialog from './Dialog/editStaffDialog'
import StaffHeader from '../../header/staffHeader'
import { returnRoleName } from '@/modules/helper/staffHelper'
import { StaffLevelRights, StaffWithLinks } from '@/types/Staff'
import Loading from '@/components/Loading/Loading'
import { useAuth } from '@/modules/context/AuthContext'
import { GET_STAFF_LIST } from '@/modules/API/staff/staffHandler'
import { Role } from '@/types/Role'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import UnlinkStaffDialog from '@/container/staff/list/StaffList/Dialog/unlinkStaffDialog.tsx'

interface StaffListProps {
    staffsListData: Array<StaffWithLinks>
    refetchAllStaffList: () => Promise<void>
    canDraft: boolean
}

const StaffList: FunctionComponent<StaffListProps> = ({
    staffsListData,
    refetchAllStaffList,
    canDraft
}: StaffListProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationSuccess } = useNotification()

    const navigate: NavigateFunction = useNavigate()

    const headerClassName: string = user?.role === Role.parent ? styles.HeaderParent : styles.HeaderDaycare

    const [staffList, setStaffList] = useState<Array<StaffWithLinks>>([])

    const [isStaffEditDialogOpen, setStaffEditDialogOpen] = useState(false)
    const [staffToEdit, setStaffToEdit] = useState<StaffWithLinks | null>(null)
    const [isStaffUnlinkDialogOpen, setStaffUnlinkDialogOpen] = useState<boolean>(false)

    function isMockStaffEmail(email: string): boolean {
        const pattern: RegExp = /^staff\d+@cald\.fr$/
        return pattern.test(email)
    }

    const columnsWeb: GridColDef[] = [
        {
            field: 'firstname',
            headerName: t('firstname'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.firstname ? params.row.firstname : <Close className={styles.RedIcon} />}
                </div>
            )
        },
        {
            field: 'lastname',
            headerName: t('lastname'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.lastname ? (
                        <a
                            href={`/staff?profile=${params.row.id}`}
                            onClick={(e) => {
                                if (!(e.ctrlKey || e.metaKey)) {
                                    e.preventDefault()
                                    navigate(`/staff?profile=${params.row.id}`)
                                }
                            }}
                            className={styles.Clickable}
                        >
                            {params.row.lastname}
                        </a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={`${styles.InfoContainer} ${styles.JustifyLeft}`}>
                    {params.row.email && !isMockStaffEmail(params.row.email) ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'level',
            headerName: t('level'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => {
                const staffLevel: number | undefined = params.row.staffLinks.find(
                    (link: StaffLevelRights): boolean => link.daycareId === user?.currentDaycare?.id
                )?.level
                return (
                    <div className={styles.InfoContainer}>
                        {staffLevel ? (
                            levels.find((level) => level.id === staffLevel)?.name
                        ) : (
                            <Close
                                className={`${styles.RedIcon} ${styles.Clickable}`}
                                onClick={() => openStaffEdit(params.row)}
                            />
                        )}
                    </div>
                )
            }
        },
        {
            field: 'role',
            headerName: t('access'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => {
                const staffRights: StaffLevelRights | undefined = params.row.staffLinks.find(
                    (link: StaffLevelRights): boolean => link.daycareId === user?.currentDaycare?.id
                )
                return (
                    <div
                        className={`${styles.InfoContainer} ${styles.Clickable}`}
                        onClick={() => openStaffEdit(params.row)}
                    >
                        {returnRoleName(t, staffRights)}
                    </div>
                )
            }
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 300,
            minWidth: 100,
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a
                        href={`/staff?profile=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/staff?profile=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                    {user?.currentDaycareRights.canWriteStaffRights && (
                        <>
                            <Edit className={styles.Icon} onClick={() => openStaffEdit(params.row)} />
                            <LinkOff className={styles.Icon} onClick={() => openStaffUnlink(params.row)} />
                        </>
                    )}
                </div>
            )
        }
    ]

    const columnsWithoutDaycare: GridColDef[] = [
        {
            field: 'firstname',
            headerName: t('firstname'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.firstname ? params.row.firstname : <Close className={styles.RedIcon} />}
                </div>
            )
        },
        {
            field: 'lastname',
            headerName: t('lastname'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.lastname ? (
                        <a
                            href={`/staff?profile=${params.row.id}`}
                            onClick={(e) => {
                                if (!(e.ctrlKey || e.metaKey)) {
                                    e.preventDefault()
                                    navigate(`/staff?profile=${params.row.id}`)
                                }
                            }}
                            className={styles.Clickable}
                        >
                            {params.row.lastname}
                        </a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={`${styles.InfoContainer} ${styles.JustifyLeft}`}>
                    {params.row.email && !isMockStaffEmail(params.row.email) ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 300,
            minWidth: 100,
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a
                        href={`/staff?profile=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/staff?profile=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                </div>
            )
        }
    ]

    function openStaffEdit(staff: StaffWithLinks): void {
        if (staff) {
            setStaffToEdit(staff)
            setStaffEditDialogOpen(true)
        } else {
            showNotificationSuccess(t('noDaycare'))
        }
    }

    function openStaffUnlink(staff: StaffWithLinks): void {
        if (staff) {
            setStaffToEdit(staff)
            setStaffUnlinkDialogOpen(true)
        } else {
            showNotificationSuccess(t('noDaycare'))
        }
    }

    const columnsMobile: GridColDef[] = [
        {
            field: 'firstname',
            headerName: t('firstname'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 100,
            maxWidth: 400,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.firstname ? (
                        params.row.firstname
                    ) : (
                        <div className={styles.IconContainer}>
                            <Close className={styles.RedIcon} />
                        </div>
                    )}
                </div>
            )
        },
        {
            field: 'lastname',
            headerName: t('lastname'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 100,
            maxWidth: 400,
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.lastname ? (
                        <a
                            href={`/staff?profile=${params.row.id}`}
                            onClick={(e) => {
                                if (!(e.ctrlKey || e.metaKey)) {
                                    e.preventDefault()
                                    navigate(`/staff?profile=${params.row.id}`)
                                }
                            }}
                            className={styles.Clickable}
                        >
                            {params.row.lastname}
                        </a>
                    ) : (
                        <div className={styles.IconContainer}>
                            <Close className={styles.RedIcon} />
                        </div>
                    )}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 80,
            maxWidth: 100,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a
                        href={`/staff?profile=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/staff?profile=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                    {user?.currentDaycareRights.canWriteStaffRights && (
                        <>
                            <Edit className={styles.Icon} onClick={() => openStaffEdit(params.row)} />
                            <LinkOff className={styles.Icon} onClick={() => openStaffUnlink(params.row)} />
                        </>
                    )}
                </div>
            )
        }
    ]

    const [levels, setLevels] = useState<Array<StaffLevel>>([])

    const [getLevels, { data: dataLevels }] = useLazyQuery(GET_STAFF_LEVELS, {
        variables: {
            daycareId: user?.currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataLevels) {
            setLevels(dataLevels.getStaffLevels)
        }
    }, [dataLevels])

    useEffect(() => {
        setLevels([])
        if (user?.currentDaycareRights.hasStaffAccess) {
            getLevels()
        } else {
            setStaffList([])
            setLevels([])
        }
    }, [user?.currentDaycare])

    const [loadingGetAllStaffInAllDaycares, setLoadingGetAllStaffInAllDaycares] = useState<boolean>(false)
    const [showOnlyWithoutDaycare, setShowOnlyWithoutDaycare] = useState<boolean>(false)

    const [getAllStaff, { data: dataStaffList }] = useLazyQuery(GET_STAFF_LIST)

    useEffect(() => {
        if (dataStaffList) {
            setStaffList(
                dataStaffList.getStaffList.filter((staff: StaffWithLinks): boolean => {
                    if (showOnlyWithoutDaycare) {
                        return staff.staffLinks.length === 0
                    }
                    return staff.staffLinks.length > 0
                })
            )
        }
    }, [dataStaffList])

    function getAllStaffInAllDaycares(showOnlyWithoutDaycare: boolean): void {
        setLoadingGetAllStaffInAllDaycares(true)
        setShowOnlyWithoutDaycare(showOnlyWithoutDaycare)

        getAllStaff().then(() => setLoadingGetAllStaffInAllDaycares(false))
    }

    useEffect(() => {
        setStaffList([])
        setStaffList(staffsListData)
    }, [staffsListData])

    return (
        <>
            <div className={styles.Header}>
                <StaffHeader
                    getAllStaffInAllDaycares={getAllStaffInAllDaycares}
                    refetchAllStaffList={refetchAllStaffList}
                    loadingGetAllStaffInAllDaycares={loadingGetAllStaffInAllDaycares}
                    canDraft={canDraft}
                />
            </div>
            <div className={styles.DataGridContainer}>
                <div className={styles.Title}>
                    <Title text={t('myStaffs')} />
                </div>
                <Desktop>
                    <StyledDataGrid
                        autoHeight
                        columns={showOnlyWithoutDaycare ? columnsWithoutDaycare : columnsWeb}
                        rows={staffList}
                        disableDensitySelector
                        disableColumnMenu
                        slots={{ toolbar: QuickSearchToolbar }}
                        showToolbar
                        sx={{ '--DataGrid-overlayHeight': '300px' }}
                        localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                        rowHeight={80}
                        className={styles.DataGrid}
                        initialState={{
                            filter: {
                                filterModel: {
                                    items: [],
                                    quickFilterExcludeHiddenColumns: false
                                }
                            }
                        }}
                        columnVisibilityModel={{
                            childsName: false
                        }}
                    />
                </Desktop>
                <Mobile>
                    <StyledDataGrid
                        autoHeight
                        columns={columnsMobile}
                        rows={staffList}
                        disableDensitySelector
                        disableColumnMenu
                        slots={{ toolbar: QuickSearchToolbar }}
                        showToolbar
                        sx={{ '--DataGrid-overlayHeight': '300px' }}
                        localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                        rowHeight={80}
                        className={styles.DataGrid}
                        initialState={{
                            filter: {
                                filterModel: {
                                    items: [],
                                    quickFilterExcludeHiddenColumns: false
                                }
                            }
                        }}
                        columnVisibilityModel={{
                            childsName: false
                        }}
                    />
                </Mobile>
                {user && isStaffEditDialogOpen && (
                    <EditStaffDialog
                        open={isStaffEditDialogOpen}
                        handleClose={() => setStaffEditDialogOpen(false)}
                        staff={staffToEdit}
                        levels={levels}
                        currentDaycare={user.currentDaycare}
                        self={staffToEdit && user ? staffToEdit.email === user.email : false}
                        refetchAllStaffList={refetchAllStaffList}
                        canWrite={user?.currentDaycareRights.canWriteStaffRights}
                    />
                )}
                {isStaffUnlinkDialogOpen && staffToEdit && user && user.currentDaycare && (
                    <UnlinkStaffDialog
                        open={isStaffUnlinkDialogOpen}
                        handleClose={() => setStaffUnlinkDialogOpen(false)}
                        staffEmail={staffToEdit.email}
                        daycareId={user.currentDaycare.id}
                        refetchAllStaffList={refetchAllStaffList}
                    />
                )}
            </div>
            {loadingGetAllStaffInAllDaycares && <Loading overall={true} />}
            <ButtonTextIcon
                type={'custom'}
                onClick={() => navigate('/calendar?tab=staff')}
                customText={t('backToPlanning')}
                customIcon={<ArrowBack />}
            />
        </>
    )
}

export default StaffList
